import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarDays, Share2, FileDown, ArrowLeft, Map as MapIcon, ExternalLink } from "lucide-react";
import api, { fileUrl, img } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function MarketDetail() {
  const { id } = useParams();
  const { t, L } = useLang();
  const [m, setM] = useState(null);
  const [mvendors, setMvendors] = useState([]);

  useEffect(() => {
    api.get(`/markets/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
    api.get(`/markets/${id}/vendors`).then((r) => setMvendors(r.data)).catch(() => {});
  }, [id]);

  if (m === false) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk">Not found.</div>;
  if (!m) return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-fk animate-pulse">Loading…</div>;

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: L(m.title_zh, m.title_en), url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success(L("链接已复制", "Link copied")); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <Link to="/markets" className="font-mono-fk text-sm inline-flex items-center gap-1 mb-6 hover:underline"><ArrowLeft size={15} /> {t("markets_title")}</Link>
      <img src={img(m.cover_image)} alt={m.title_en} className="rounded-2xl fk-border-thick w-full h-80 object-cover" />
      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <span className={`font-mono-fk text-xs fk-border rounded-full px-3 py-1 ${m.status === "upcoming" ? "bg-[#00E5FF]" : "bg-white"}`}>
            {t(m.status === "upcoming" ? "upcoming" : "past")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl mt-4">{L(m.title_zh, m.title_en)}</h1>
          <p className="mt-5 text-black/70 leading-relaxed text-lg">{L(m.description_zh, m.description_en)}</p>
          {m.images?.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {m.images.map((img2, i) => <img key={i} src={fileUrl(img2)} alt="" className="rounded-xl fk-border h-32 w-full object-cover" />)}
            </div>
          )}

          {(m.map_image || m.map_url) && (
            <div className="mt-8" data-testid="market-map">
              <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-3 flex items-center gap-2"><MapIcon size={15} /> {L("摊位分布图 / 地图", "BOOTH MAP / LOCATION")}</div>
              {m.map_image && <img src={fileUrl(m.map_image)} alt="market map" className="rounded-2xl fk-border-thick w-full object-cover" />}
              {m.map_url && (
                <a href={m.map_url} target="_blank" rel="noreferrer" data-testid="market-map-link" className="mt-3 inline-flex items-center gap-2 fk-border rounded-full px-5 py-2.5 font-semibold bg-[#00E5FF] fk-lift">
                  <ExternalLink size={16} /> {L("在 Google 地图打开", "Open in Google Maps")}
                </a>
              )}
            </div>
          )}

          {mvendors.length > 0 && (
            <div className="mt-8" data-testid="market-vendors">
              <div className="font-mono-fk text-xs tracking-widest text-[#FF5757] mb-3">{L("参与摊主", "PARTICIPATING VENDORS")} · {mvendors.length}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {mvendors.map((v) => (
                  <Link key={v.id} to={`/vendors/${v.slug}`} data-testid={`market-vendor-${v.id}`} className="block bg-white fk-border rounded-xl overflow-hidden fk-lift">
                    <img src={img(v.photos?.[0])} alt="" className="w-full h-24 object-cover border-b-2 border-black" />
                    <div className="p-2">
                      <div className="font-semibold text-sm truncate">{L(v.name_zh, v.name_en)}</div>
                      <div className="text-xs text-black/50 truncate">{v.category}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="bg-white fk-border-thick rounded-2xl p-6 fk-shadow h-fit space-y-4">
          <div className="font-mono-fk text-sm space-y-2">
            <div className="flex items-center gap-2"><CalendarDays size={16} /> {m.date}</div>
            <div className="flex items-center gap-2"><MapPin size={16} /> {L(m.location_zh, m.location_en)}</div>
          </div>
          {m.apply_open && (
            <Link to={`/apply?market=${m.id}`} data-testid="market-detail-apply" className="block text-center fk-border rounded-full px-5 py-3 font-semibold bg-[#FFD93D] fk-lift">
              {t("apply_join")}
            </Link>
          )}
          {m.proposal_url && (
            <a href={fileUrl(m.proposal_url)} target="_blank" rel="noreferrer" data-testid="market-proposal" className="flex items-center justify-center gap-2 fk-border rounded-full px-5 py-3 font-semibold bg-[#00E5FF] fk-lift">
              <FileDown size={16} /> {t("download_proposal")}
            </a>
          )}
          <button onClick={share} className="w-full flex items-center justify-center gap-2 fk-border rounded-full px-5 py-3 font-semibold bg-white fk-lift">
            <Share2 size={16} /> {t("share")}
          </button>
        </aside>
      </div>
    </div>
  );
}
